import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Docked "Dynamic Island" workflow stepper — ported from
 * QC-Batch-Prototype.html's newer design, where the step pill lives directly
 * inside the app's own topbar (centered between the hamburger and the
 * badge/avatar group — see AppShell.tsx's `#topbar-stepper-slot` +
 * shell.css's `.topbar .steps` rules), not a separate full-width bar above
 * the whole shell. Visible on every screen in the Analysis Queue → QC Batch →
 * SDMS flow. Shared by AnalysisListPage (step 0 active) and
 * QcBatchDetailPage (steps 1-8, computed from live form state) so the same
 * pill — same steps, same styling — follows the user across both screens.
 */
export type StepState = 'todo' | 'active' | 'done' | 'optional';

export const WORKFLOW_STEPS = [
  'Select test', 'New', 'Job ID', 'Instrument', 'Load', 'Mark samples', 'Sort', 'Others', 'Open SDMS',
];

/**
 * `steps` overrides the default labels — the QC Batch detail passes the
 * PICK-MODE list (Select matrix · Test · Method · Job ID · …) when the batch
 * is created from the QC Batches list's New (no pre-seeded test), and the
 * default WORKFLOW_STEPS when arriving from the Analysis Queue (user request
 * 2026-07-08: the island must mirror the actual entry flow).
 */
export function WorkflowStepperBar({ stepStatus, steps = WORKFLOW_STEPS }:
  { stepStatus: StepState[]; steps?: readonly string[] }) {
  // The slot lives inside AppShell, which may mount after this component on
  // first paint — re-check on mount rather than reading the DOM only once.
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // AppShell renders the stepper slot as #ai-topbar-stepper; the QC base used
    // #topbar-stepper-slot which doesn't exist here, so the pill never showed. (Fix 2026-07-07.)
    setSlot(document.getElementById('ai-topbar-stepper'));
    // Hides the topbar's breadcrumb/title while a stepper is docked, matching
    // QC-Batch-Prototype.html's real topbar (hamburger + centered Island +
    // badge/avatar only — see shell.css `body.qcb-stepper-active .tb-left`).
    document.body.classList.add('qcb-stepper-active');
    return () => document.body.classList.remove('qcb-stepper-active');
  }, []);

  if (!slot) return null;

  return createPortal(
    <div className="steps">
      {steps.map((l, i) => {
        const s = stepStatus[i];
        return (
          <span key={l} style={{ display: 'contents' }}>
            <div className={`step ${s}`} title={l}>
              <span className="num">{s === 'done' ? '✓' : i + 1}</span>{l}
            </div>
            {i < steps.length - 1 && <span className={`bar ${s === 'done' ? 'done' : ''}`} />}
          </span>
        );
      })}
    </div>,
    slot,
  );
}
